import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { squadService } from '../../../services/socialService';
import { supabase } from '../../../lib/supabase';

const CreateSquadModal = ({ isOpen, onClose, onSquadCreated, userId }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    icon: 'Users',
    color: 'primary'
  });

  const iconOptions = [
    { name: 'Users', label: 'Equipo' },
    { name: 'Zap', label: 'Energía' },
    { name: 'Target', label: 'Meta' },
    { name: 'Flame', label: 'Fuego' },
    { name: 'Heart', label: 'Corazón' },
    { name: 'Star', label: 'Estrella' },
    { name: 'Shield', label: 'Escudo' },
    { name: 'Rocket', label: 'Cohete' }
  ];

  const colorOptions = [
    { value: 'primary', label: 'Azul', class: 'bg-primary' },
    { value: 'success', label: 'Verde', class: 'bg-success' },
    { value: 'warning', label: 'Naranja', class: 'bg-warning' },
    { value: 'error', label: 'Rojo', class: 'bg-error' },
    { value: 'accent', label: 'Morado', class: 'bg-accent' }
  ];

  const handleCreateSquad = async () => {
    if (!formData?.name?.trim()) return;

    setLoading(true);
    setError(null);
    try {
      // Verify user profile exists
      const { data: profile, error: profileError } = await supabase
        ?.from('user_profiles')
        ?.select('id')
        ?.eq('id', userId)
        ?.single();

      if (profileError || !profile) {
        setError('Tu perfil de usuario no está configurado correctamente. Por favor, cierra sesión y vuelve a iniciar sesión.');
        setLoading(false);
        return;
      }

      await squadService?.createSquad(userId, formData);
      onSquadCreated();
      resetForm();
    } catch (error) {
      console.error('Error creating squad:', error);
      if (error?.code === '23503') {
        setError('Error: Tu perfil no está sincronizado. Por favor, recarga la página e intenta nuevamente.');
      } else {
        setError('Error al crear el squad. Por favor, intenta nuevamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      icon: 'Users',
      color: 'primary'
    });
    setError(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-fade-in"
        onClick={handleClose}
      />
      
      <div className="relative w-full max-w-2xl bg-card rounded-xl shadow-wellness-lg animate-scale-in elevation-3 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border/40">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Crear Squad</h2>
            <p className="text-sm text-muted-foreground">Forma un grupo de disciplina y wellness</p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClose}
            className="text-muted-foreground hover:text-foreground"
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          <div className="space-y-6">
            {/* Error Message */}
            {error && (
              <div className="p-4 bg-error/10 border border-error/20 rounded-lg">
                <div className="flex items-start gap-3">
                  <Icon name="AlertCircle" size={20} className="text-error mt-0.5" />
                  <p className="text-sm text-error">{error}</p>
                </div>
              </div>
            )}

            {/* Squad Name */}
            <Input
              label="Nombre del Squad"
              value={formData?.name}
              onChange={(e) => setFormData({ ...formData, name: e?.target?.value })}
              placeholder="Ej: Guerreros del Gym"
              required
            />

            {/* Squad Description */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Descripción
              </label>
              <textarea
                value={formData?.description}
                onChange={(e) => setFormData({ ...formData, description: e?.target?.value })}
                placeholder="Describe el propósito de tu squad..."
                rows={3}
                className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
              />
            </div>

            {/* Icon Selection */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-3">
                Ícono del Squad
              </label>
              <div className="grid grid-cols-4 gap-3">
                {iconOptions?.map((icon) => (
                  <button
                    key={icon?.name}
                    onClick={() => setFormData({ ...formData, icon: icon?.name })}
                    className={`p-4 flex flex-col items-center space-y-2 border-2 rounded-xl transition-all ${
                      formData?.icon === icon?.name
                        ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                    }`}
                  >
                    <Icon name={icon?.name} size={24} className="text-foreground" />
                    <span className="text-xs text-muted-foreground">{icon?.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Color Selection */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-3">
                Color del Squad
              </label>
              <div className="flex space-x-3">
                {colorOptions?.map((color) => (
                  <button
                    key={color?.value}
                    onClick={() => setFormData({ ...formData, color: color?.value })}
                    className={`flex-1 p-4 flex flex-col items-center space-y-2 border-2 rounded-xl transition-all ${
                      formData?.color === color?.value
                        ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-full ${color?.class}`} />
                    <span className="text-xs text-muted-foreground">{color?.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Preview */}
            <div className="p-4 bg-muted/30 rounded-xl border border-border">
              <p className="text-sm font-medium text-foreground mb-3">Vista Previa</p>
              <div className="flex items-center space-x-3">
                <div className={`w-12 h-12 rounded-full bg-${formData?.color}/10 flex items-center justify-center`}>
                  <Icon name={formData?.icon} size={24} className={`text-${formData?.color}`} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground">
                    {formData?.name || 'Nombre del Squad'}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {formData?.description || 'Descripción del squad'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex space-x-3 p-6 border-t border-border/40">
          <Button
            variant="outline"
            onClick={handleClose}
            fullWidth
          >
            Cancelar
          </Button>
          <Button
            onClick={handleCreateSquad}
            disabled={!formData?.name?.trim() || loading}
            loading={loading}
            fullWidth
          >
            Crear Squad
          </Button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default CreateSquadModal;